import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Code as CodeIcon,
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import grapesjs, { Editor } from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import grapesjsPresetWebpage from 'grapesjs-preset-webpage';

const STORAGE_KEY = 'grapesjs-html-content';
const CKEDITOR_CDN = 'https://cdn.ckeditor.com/4.22.1/standard-all/ckeditor.js';

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if ((window as any).CKEDITOR) { resolve(); return; }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });

// Tags that CKEditor.inline() can't handle — it only supports block elements
const CK_UNSUPPORTED_TAGS = new Set([
  'SPAN', 'A', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'SUB', 'SUP',
  'ABBR', 'CODE', 'SMALL', 'MARK', 'LABEL', 'BR', 'IMG',
]);

const setupCKEditorRTE = (gjsEditor: Editor) => {
  const CK = (window as any).CKEDITOR;
  if (!CK) return;

  gjsEditor.setCustomRte({
    enable(el, rte) {
      const htmlEl = el as HTMLElement;
      htmlEl.contentEditable = 'true';

      // For inline elements, return a lightweight shim so GrapeJS still
      // tracks the editing state and calls getContent() on disable.
      if (CK_UNSUPPORTED_TAGS.has(htmlEl.tagName)) {
        htmlEl.focus();
        return {
          status: 'ready',
          fire: () => {},
          focusManager: { blur: () => {} },
          getData: () => htmlEl.innerHTML,
        };
      }

      if (rte && rte.status !== 'destroyed') {
        rte.fire('focus');
        return rte;
      }

      const rteToolbar = gjsEditor.RichTextEditor.getToolbarEl();
      rte = CK.inline(el, {
        startupFocus: true,
        versionCheck: false,
        extraPlugins: 'sharedspace,colorbutton,font,justify',
        removePlugins: 'floatingspace,magicline',
        toolbar: [
          { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'RemoveFormat'] },
          { name: 'colors', items: ['TextColor', 'BGColor'] },
          { name: 'styles', items: ['Font', 'FontSize'] },
          { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight'] },
          { name: 'links', items: ['Link', 'Unlink'] },
        ],
        sharedSpaces: { top: rteToolbar },
        allowedContent: true,
        extraAllowedContent: '*(*);*{*}',
      });

      return rte;
    },

    disable(el, rte) {
      (el as HTMLElement).contentEditable = 'false';
      if (rte?.focusManager) {
        rte.focusManager.blur(true);
      }
    },

    getContent(el, rte) {
      return rte?.getData?.() || (el as HTMLElement).innerHTML;
    },
  });
};

export const HtmlEditor: React.FC = () => {
  const editorRef = useRef<Editor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [htmlCode, setHtmlCode] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [themeVars, setThemeVars] = useState<Array<{ name: string; value: string; isColor: boolean }>>([]);

  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    let unmounted = false;

    (async () => {
      // Load CKEditor for reliable rich-text formatting (bold, color, font, etc.)
      try { await loadScript(CKEDITOR_CDN); } catch { /* falls back to built-in RTE */ }
      if (unmounted || !containerRef.current) return;

      const initialHtml = localStorage.getItem(STORAGE_KEY)
        || sessionStorage.getItem(STORAGE_KEY)
        || '';
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);

      const editor = grapesjs.init({
        container: containerRef.current,
        height: '100%',
        width: 'auto',
        storageManager: false,
        plugins: [grapesjsPresetWebpage],
        pluginsOpts: {
          [grapesjsPresetWebpage as any]: {
            blocksBasicOpts: { flexGrid: true },
            countdownOpts: false,
            formsOpts: false,
          },
        },
        canvas: {
          styles: [],
          scripts: [],
        },
        deviceManager: {
          devices: [
            { name: 'Desktop', width: '' },
            { name: 'Tablet', width: '768px', widthMedia: '992px' },
            { name: 'Mobile', width: '375px', widthMedia: '480px' },
          ],
        },
      });

      // Replace the flaky built-in RTE with CKEditor
      const CK = (window as any).CKEDITOR;
      if (CK) {
        CK.config.versionCheck = false;
        setupCKEditorRTE(editor);
      }

      if (initialHtml) {
        loadHtmlIntoEditor(editor, initialHtml);
      }

      // Show Style Manager by default (colors, fonts, dimensions) instead of Blocks
      const smBtn = editor.Panels.getButton('views', 'open-sm');
      if (smBtn) smBtn.set('active', true);

      editorRef.current = editor;
    })();

    return () => {
      unmounted = true;
      // Clean up any CKEditor instances
      const CK = (window as any).CKEDITOR;
      if (CK?.instances) {
        Object.values(CK.instances).forEach((inst: any) => {
          try { inst.destroy(); } catch { /* already destroyed */ }
        });
      }
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, []);

  const loadHtmlIntoEditor = (editor: Editor, html: string) => {
    const fullDoc = html.includes('<html') || html.includes('<!DOCTYPE');
    if (fullDoc) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const styles = Array.from(doc.querySelectorAll('style'))
        .map((s) => s.textContent || '')
        .join('\n');
      const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))
        .map((l) => l.getAttribute('href'))
        .filter(Boolean) as string[];

      doc.querySelectorAll('style').forEach((s) => s.remove());
      doc.querySelectorAll('link[rel="stylesheet"]').forEach((l) => l.remove());
      doc.querySelectorAll('script').forEach((s) => s.remove());

      const bodyHtml = doc.body?.innerHTML || html;

      editor.setComponents(bodyHtml);
      if (styles) {
        editor.setStyle(styles);
      }
      // Inject external stylesheets into the canvas after it's ready
      editor.on('canvas:frame:load', () => {
        links.forEach((href) => {
          const canvasDoc = editor.Canvas.getDocument();
          if (canvasDoc?.head) {
            canvasDoc.head.insertAdjacentHTML(
              'beforeend',
              `<link rel="stylesheet" href="${href}" />`
            );
          }
        });
      });
      // Also try immediately in case the frame is already loaded
      links.forEach((href) => {
        try {
          const canvasDoc = editor.Canvas.getDocument();
          if (canvasDoc?.head) {
            canvasDoc.head.insertAdjacentHTML(
              'beforeend',
              `<link rel="stylesheet" href="${href}" />`
            );
          }
        } catch (_) { /* frame not ready yet, the event handler will cover it */ }
      });
    } else {
      editor.setComponents(html);
    }
  };

  const getFullHtml = (): string => {
    const editor = editorRef.current;
    if (!editor) return '';

    // Deselect active component to commit any pending CKEditor changes
    // back to the component model before reading content.
    editor.select();

    // Read directly from the canvas iframe — this preserves all edits,
    // CSS variables, Google Fonts links, and the full original stylesheet
    // that editor.getCss() would lose.
    const frame = editor.Canvas.getFrameEl() as HTMLIFrameElement | null;
    const doc = frame?.contentDocument;
    if (doc?.documentElement) {
      const clone = doc.documentElement.cloneNode(true) as HTMLElement;

      // Strip GrapeJS tracking attributes and editor artefacts
      clone.querySelectorAll('*').forEach((el) => {
        for (const attr of Array.from(el.attributes)) {
          if (attr.name.startsWith('data-gjs') || attr.name === 'contenteditable') {
            el.removeAttribute(attr.name);
          }
        }
      });

      return '<!DOCTYPE html>\n' + clone.outerHTML;
    }

    // Fallback: reconstruct from GrapeJS API
    const html = editor.getHtml();
    const css = editor.getCss();
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>${css}</style>
</head>
<body>${html}</body>
</html>`;
  };

  const handleDownload = () => {
    const fullHtml = getFullHtml();
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'page.html';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setSnackbar({ open: true, message: 'HTML file downloaded', severity: 'success' });
  };

  const handleCopyHtml = () => {
    const fullHtml = getFullHtml();
    navigator.clipboard.writeText(fullHtml).then(() => {
      setSnackbar({ open: true, message: 'HTML copied to clipboard', severity: 'success' });
    });
  };

  const handleViewCode = () => {
    setHtmlCode(getFullHtml());
    setCodeDialogOpen(true);
  };

  const handleApplyCode = () => {
    const editor = editorRef.current;
    if (!editor) return;
    loadHtmlIntoEditor(editor, htmlCode);
    setCodeDialogOpen(false);
    setSnackbar({ open: true, message: 'HTML applied to editor', severity: 'success' });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // ── Theme Editor (CSS variable color pickers) ──────────────────────

  const toHex6 = (val: string): string => {
    if (/^#[0-9a-f]{6}$/i.test(val)) return val;
    if (/^#[0-9a-f]{3}$/i.test(val)) {
      return '#' + val[1] + val[1] + val[2] + val[2] + val[3] + val[3];
    }
    const m = val.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return '#' + [m[1], m[2], m[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
    return '#000000';
  };

  const openThemeDialog = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const vars: Array<{ name: string; value: string; isColor: boolean }> = [];

    // Read CSS variables from the canvas iframe's :root rule
    const canvasDoc = editor.Canvas.getDocument();
    if (canvasDoc) {
      try {
        for (const sheet of Array.from(canvasDoc.styleSheets)) {
          for (const rule of Array.from((sheet as CSSStyleSheet).cssRules)) {
            if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
              for (let i = 0; i < rule.style.length; i++) {
                const prop = rule.style[i];
                if (prop.startsWith('--')) {
                  const val = rule.style.getPropertyValue(prop).trim();
                  const isColor = /^#[0-9a-f]{3,8}$/i.test(val)
                    || val.startsWith('rgb')
                    || val.startsWith('hsl');
                  vars.push({ name: prop, value: val, isColor });
                }
              }
            }
          }
        }
      } catch { /* cross-origin sheet */ }
    }

    // Fallback: parse from GrapeJS CSS string
    if (vars.length === 0) {
      const css = editor.getCss() || '';
      const rootMatch = css.match(/:root\s*\{([^}]+)\}/);
      if (rootMatch) {
        (rootMatch[1].match(/--[\w-]+\s*:\s*[^;]+/g) || []).forEach((entry) => {
          const idx = entry.indexOf(':');
          const name = entry.substring(0, idx).trim();
          const value = entry.substring(idx + 1).trim();
          const isColor = /^#[0-9a-f]{3,8}$/i.test(value) || value.startsWith('rgb') || value.startsWith('hsl');
          vars.push({ name, value, isColor });
        });
      }
    }

    if (vars.length === 0) {
      setSnackbar({ open: true, message: 'No CSS variables found in this page', severity: 'info' });
      return;
    }
    setThemeVars(vars);
    setThemeDialogOpen(true);
  };

  const handleThemeVarChange = useCallback((name: string, newValue: string) => {
    setThemeVars((prev) => prev.map((v) => (v.name === name ? { ...v, value: newValue } : v)));

    // Live preview: update the canvas immediately
    const canvasDoc = editorRef.current?.Canvas?.getDocument();
    if (canvasDoc) {
      canvasDoc.documentElement.style.setProperty(name, newValue);
    }
  }, []);

  const applyThemeChanges = () => {
    const editor = editorRef.current;
    if (!editor) return;

    // Update the stylesheet :root rule in the canvas for persistence
    const canvasDoc = editor.Canvas.getDocument();
    if (canvasDoc) {
      try {
        for (const sheet of Array.from(canvasDoc.styleSheets)) {
          for (const rule of Array.from((sheet as CSSStyleSheet).cssRules)) {
            if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
              themeVars.forEach((v) => rule.style.setProperty(v.name, v.value));
            }
          }
        }
      } catch { /* cross-origin */ }
      // Clear inline overrides used for live preview
      themeVars.forEach((v) => canvasDoc.documentElement.style.removeProperty(v.name));
    }

    // Also update GrapeJS CSS model
    let css = editor.getCss() || '';
    const rootBlock = `:root{\n${themeVars.map((v) => `  ${v.name}:${v.value};`).join('\n')}\n}`;
    if (css.includes(':root')) {
      css = css.replace(/:root\s*\{[^}]+\}/, rootBlock);
    }
    editor.setStyle(css);

    setThemeDialogOpen(false);
    setSnackbar({ open: true, message: 'Theme updated', severity: 'success' });
  };

  const themeVarLabel = (name: string) =>
    name.replace(/^--/, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // ───────────────────────────────────────────────────────────────────

  const applyZoom = useCallback((level: number) => {
    const editor = editorRef.current;
    if (!editor) return;
    const clamped = Math.max(25, Math.min(200, level));
    setZoomLevel(clamped);
    const scale = clamped / 100;

    editor.Canvas.setZoom(clamped);

    // Resize the frames container so the zoomed content fills the canvas
    // viewport. The critical piece is transform-origin: 0 0 — without it,
    // the default center-center origin causes the content to shift off-screen.
    const canvasEl = editor.Canvas.getElement();
    const framesEl = editor.Canvas.getFramesEl();
    if (canvasEl && framesEl) {
      if (clamped === 100) {
        framesEl.style.width = '';
        framesEl.style.height = '';
        framesEl.style.transformOrigin = '';
      } else {
        const rect = canvasEl.getBoundingClientRect();
        framesEl.style.transformOrigin = '0 0';
        framesEl.style.width = `${rect.width / scale}px`;
        framesEl.style.height = `${rect.height / scale}px`;
      }
    }
  }, []);

  const handleZoomIn = () => applyZoom(zoomLevel + 10);
  const handleZoomOut = () => applyZoom(zoomLevel - 10);
  const handleZoomReset = () => applyZoom(100);
  const handleFitViewport = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const framesEl = editor.Canvas.getFramesEl();
    if (framesEl) {
      framesEl.style.width = '';
      framesEl.style.height = '';
      framesEl.style.transformOrigin = '';
    }
    editor.Canvas.fitViewport();
    const actual = Math.round(editor.Canvas.getZoom());
    setZoomLevel(actual);
    const scale = actual / 100;
    const canvasEl = editor.Canvas.getElement();
    if (canvasEl && framesEl && actual !== 100) {
      const rect = canvasEl.getBoundingClientRect();
      framesEl.style.transformOrigin = '0 0';
      framesEl.style.width = `${rect.width / scale}px`;
      framesEl.style.height = `${rect.height / scale}px`;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', mx: -3, mt: -3, mb: -3 }}>
      <AppBar position="static" color="default" elevation={1} sx={{ zIndex: 10 }}>
        <Toolbar variant="dense" sx={{ gap: 0.5 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton edge="start" onClick={() => navigate(-1)} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 'auto' }}>
            HTML Page Editor
          </Typography>

          {/* Zoom controls */}
          <Tooltip title="Zoom Out (−)">
            <IconButton size="small" onClick={handleZoomOut} disabled={zoomLevel <= 25}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Click to reset to 100%">
            <Button
              size="small"
              onClick={handleZoomReset}
              sx={{ textTransform: 'none', minWidth: 48, px: 0.5, fontWeight: 600, fontSize: '0.8rem' }}
            >
              {zoomLevel}%
            </Button>
          </Tooltip>
          <Tooltip title="Zoom In (+)">
            <IconButton size="small" onClick={handleZoomIn} disabled={zoomLevel >= 200}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fit to Screen">
            <IconButton size="small" onClick={handleFitViewport}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Edit Theme Colors & Variables">
            <Button
              startIcon={<PaletteIcon />}
              size="small"
              onClick={openThemeDialog}
              sx={{ textTransform: 'none' }}
            >
              Theme
            </Button>
          </Tooltip>
          <Tooltip title="View / Edit Source Code">
            <Button
              startIcon={<CodeIcon />}
              size="small"
              onClick={handleViewCode}
              sx={{ textTransform: 'none' }}
            >
              Source
            </Button>
          </Tooltip>
          <Tooltip title="Copy HTML to Clipboard">
            <Button
              startIcon={<CopyIcon />}
              size="small"
              onClick={handleCopyHtml}
              sx={{ textTransform: 'none' }}
            >
              Copy
            </Button>
          </Tooltip>
          <Tooltip title="Download HTML File">
            <Button
              startIcon={<DownloadIcon />}
              size="small"
              variant="outlined"
              onClick={handleDownload}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'hidden',
          '& .gjs-one-bg': { background: '#f5f5f5' },
          '& .gjs-two-color': { color: '#333' },
          '& .gjs-pn-views-container, & .gjs-pn-views, & .gjs-pn-options': {
            background: '#fff',
          },
          // Hide GrapeJS's built-in RTE buttons (B/I/U/S/link/wrap) so only
          // CKEditor's toolbar shows. We keep them in the DOM so the toolbar
          // container stays visible for CKEditor's sharedSpaces.
          '& .gjs-rte-action': { display: 'none !important' },
        }}
      />

      {/* Source Code Dialog */}
      <Dialog
        open={codeDialogOpen}
        onClose={() => setCodeDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>HTML Source Code</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            minRows={20}
            maxRows={35}
            value={htmlCode}
            onChange={(e) => setHtmlCode(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyCode}>
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Theme Editor Dialog */}
      <Dialog
        open={themeDialogOpen}
        onClose={() => setThemeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Theme Colors &amp; Variables</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit CSS variables defined in <code>:root</code>. Changes preview live in the canvas.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {themeVars.map((v) => (
              <Box key={v.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    minWidth: 140,
                    fontFamily: 'monospace',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {themeVarLabel(v.name)}
                </Typography>
                {v.isColor ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="color"
                      value={toHex6(v.value)}
                      onChange={(e) => handleThemeVarChange(v.name, e.target.value)}
                      style={{
                        width: 36,
                        height: 30,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                    <TextField
                      size="small"
                      value={v.value}
                      onChange={(e) => handleThemeVarChange(v.name, e.target.value)}
                      sx={{ width: 120, '& input': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                    />
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    value={v.value}
                    onChange={(e) => handleThemeVarChange(v.name, e.target.value)}
                    sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyThemeChanges}>
            Apply Theme
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HtmlEditor;
